import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Trash2, X } from "lucide-react";

/**
 * Admin-only danger button — wipes every transactional record from the DB
 * (bookings, invoices, payments, wallet txns, donations, parents, wards, etc.).
 * Content (services, CMS, batches, testimonials, homepage, NGO profile) is preserved.
 */
export default function ResetTestDataButton() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (confirm !== "RESET") { toast.error("Please type RESET (all caps) to confirm."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-test-data", { confirm_phrase: "RESET" });
      setResult(data);
      toast.success("Test data cleared");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setConfirm(""); setResult(null); }}
        data-testid="reset-test-data-btn"
        className="border border-red-500/50 text-red-400 px-4 py-2 text-xs font-mono uppercase tracking-widest hover:bg-red-500 hover:text-white flex items-center gap-2"
      >
        <Trash2 size={13} /> Reset Test Data
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-[#0B1120] border border-red-500/50 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-modal">
            <div className="px-5 py-4 border-b border-white/12 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-red-400" size={22} />
                <div className="font-display text-lg font-black uppercase tracking-tight">Reset Test Data</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>

            {!result ? (
              <div className="p-5 space-y-4">
                <p className="text-sm text-white/80">
                  This will <b className="text-red-400">permanently delete</b> every transactional record from the database:
                </p>
                <ul className="text-xs text-white/70 space-y-1 pl-4">
                  <li>• All bookings, invoices, payments, transactions</li>
                  <li>• All ward wallet transactions and donations</li>
                  <li>• All parent accounts, wards, enrollments, attendance</li>
                  <li>• All tournament team registrations</li>
                  <li>• All contact messages and enrolment enquiries</li>
                  <li>• All push subscriptions and audit logs</li>
                </ul>
                <div className="bg-green-500/10 border border-green-500/40 p-3">
                  <p className="text-xs text-green-300">
                    <b>Kept intact:</b> admin/staff/coach accounts, all CMS content
                    (homepage, academy page, admission fees, gallery, blogs, testimonials, tournaments),
                    NGO profile, payment gateway config, batches & services.
                  </p>
                </div>
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-1">Type <b className="text-red-400">RESET</b> to confirm</div>
                  <input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                    autoFocus placeholder="RESET" className="w-full bg-transparent border border-red-500/40 py-3 px-4 font-mono uppercase text-center text-lg tracking-widest" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setOpen(false)} className="flex-1 border border-white/20 py-3 text-xs font-mono uppercase tracking-widest hover:bg-white/6">Cancel</button>
                  <button onClick={submit} disabled={busy || confirm !== "RESET"} data-testid="reset-confirm-btn"
                    className="flex-1 bg-red-500 text-white py-3 font-bold uppercase tracking-widest text-xs hover:bg-red-400 disabled:opacity-50">
                    {busy ? "Clearing…" : "Yes, Wipe Test Data"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-5">
                <div className="text-green-400 text-sm font-mono uppercase tracking-widest mb-3">✓ Reset complete</div>
                <p className="text-sm text-white/70 mb-3">{result.message}</p>
                <div className="bg-black/40 border border-white/10 p-3 max-h-72 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {Object.entries(result.cleared || {}).map(([k, v]) => (
                        <tr key={k} className="border-b border-white/6 last:border-b-0">
                          <td className="py-1.5 font-mono text-white/70">{k}</td>
                          <td className="py-1.5 text-right font-mono brand-orange">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-white/50 mt-3">
                  Now redeploy to production (Deploy button top-right) so users get a fresh, empty starting point.
                </p>
                <button onClick={() => setOpen(false)} className="mt-4 w-full border border-white/20 py-2.5 text-xs font-mono uppercase tracking-widest hover:bg-white/6">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
